import { useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { fmtCur } from "../../renewals/format";
import { usePartners, type PartnerRow } from "../api";
import { PartnerDrawer } from "../components/PartnerDrawer";

function levelClasses(level: string): string {
  // Partner tier — top tier gets power weight, mid tier reads as brand
  // identity, bottom tier is neutral.
  if (level === "Cinch Platinum") return "bg-power-light text-power";
  if (level === "Certified") return "bg-brand-light text-brand";
  return "bg-muted text-muted-foreground";
}

function statusBadge(s: string | null) {
  if (!s) return null;
  const map: Record<string, string> = {
    Active: "bg-success-light text-success",
    "At Risk": "bg-warning-light text-warning",
    Churned: "bg-danger-light text-danger",
  };
  return (
    <span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", map[s] ?? "bg-muted")}>
      {s}
    </span>
  );
}

export function PartnersPage() {
  const navigate = useNavigate();
  const q = usePartners();
  const [search, setSearch] = useState("");
  const [hideChurned, setHideChurned] = useState(true);

  const allRows = useMemo<PartnerRow[]>(() => q.data?.rows ?? [], [q.data]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return allRows.filter((r) => {
      if (
        hideChurned &&
        r.billing_status === "Churned" &&
        (r.last_3_periods_billing ?? 0) === 0
      ) {
        return false;
      }
      if (!term) return true;
      const hay = `${r.account_name} ${r.qb_id} ${r.industry_name ?? ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [allRows, search, hideChurned]);

  const hiddenCount = allRows.length - filtered.length;

  // Roster KPIs are computed on the unfiltered population so they read as
  // book-level totals, not "what the filter currently shows".
  const totalArr = allRows.reduce((s, r) => s + (r.current_arr ?? 0), 0);
  const totalRunRate = allRows.reduce((s, r) => s + (r.run_rate_arr ?? 0), 0);
  const totalPOs = allRows.reduce((s, r) => s + (r.po_count ?? 0), 0);
  const totalCompanies = allRows.reduce((s, r) => s + (r.company_count ?? 0), 0);
  const currentPeriod = allRows[0]?.current_period ?? "—";

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Partners</h1>
        <p className="text-sm text-muted-foreground">
          Resellers & licensees — master contracts, Purchase Orders, and rolled-up sub-customer
          ARR. <b>Current Annualized</b> = latest month's total billing × 12. Window anchor:{" "}
          <code>{currentPeriod}</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label="Partners"
          value={allRows.length}
          sub="active reseller/license accounts"
        />
        <KpiTile
          label="Total ARR"
          value={fmtCur(totalArr)}
          sub={`as of ${currentPeriod}`}
        />
        <KpiTile
          label="Total run rate"
          value={fmtCur(totalRunRate)}
          sub="trailing 3-month avg × 12"
        />
        <KpiTile
          label="Sub-customer POs"
          value={totalPOs}
          sub={`${totalCompanies} companies linked`}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search partner name, QB ID, industry…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hideChurned}
            onChange={(e) => setHideChurned(e.target.checked)}
          />
          Hide churned partners with no billing in the last 3 months
          {hideChurned && hiddenCount > 0 && (
            <span className="text-xs text-muted-foreground">({hiddenCount} hidden)</span>
          )}
        </label>
        <span className="ml-auto text-xs text-muted-foreground">{filtered.length} partners</span>
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading partners…</p>}
      {q.error && <p className="text-sm text-destructive">{String(q.error)}</p>}

      {q.data && (
        <Card>
          <CardContent className="p-0">
            <Table containerClassName="max-h-[calc(100vh-22rem)]">
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead align="right">Current ARR</TableHead>
                  <TableHead align="right">YTD Δ</TableHead>
                  <TableHead align="right">12-mo Δ</TableHead>
                  <TableHead align="right">Run rate</TableHead>
                  <TableHead align="right">POs</TableHead>
                  <TableHead align="right">Companies</TableHead>
                  <TableHead>Closer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody zebra>
                {filtered.length === 0 && (
                  <TableEmpty colSpan={10}>No partner accounts match.</TableEmpty>
                )}
                {filtered.map((r) => (
                  <TableRow
                    key={r.account_id}
                    clickable
                    onClick={() =>
                      navigate({
                        to: "/partners",
                        search: { partner: r.account_id },
                      })
                    }
                  >
                    <TableCell>
                      {/* Row click → quick view; explicit name click →
                          expanded view. stopPropagation so the bubble
                          doesn't trigger the row handler. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate({
                            to: "/partners",
                            search: { partner: r.account_id, expanded: true },
                          });
                        }}
                        className="text-left font-medium text-brand hover:underline focus-visible:underline focus-visible:outline-none"
                        title="Open full partner view"
                      >
                        {r.account_name}
                      </button>
                      <div className="text-xs text-muted-foreground">
                        QB-{r.qb_id}
                        {r.industry_name && <> · {r.industry_name}</>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "rounded-md px-2 py-0.5 text-xs font-medium",
                          levelClasses(r.partner_level),
                        )}
                      >
                        {r.partner_level}
                      </span>
                    </TableCell>
                    <TableCell>{statusBadge(r.billing_status)}</TableCell>
                    <TableCell numeric className="font-medium">
                      {fmtCur(r.current_arr)}
                    </TableCell>
                    <TableCell align="right">
                      <DeltaCell value={r.arr_ytd_delta} />
                    </TableCell>
                    <TableCell align="right">
                      <DeltaCell value={r.arr_t12m_delta} />
                    </TableCell>
                    <TableCell numeric>{fmtCur(r.run_rate_arr)}</TableCell>
                    <TableCell numeric>{r.po_count}</TableCell>
                    <TableCell numeric>{r.company_count}</TableCell>
                    <TableCell className="text-muted-foreground">{r.closer_name ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <PartnerDrawer />
    </div>
  );
}
